import { UseFormReturn } from 'react-hook-form';
import { DataList, ToggleField } from '@brighte/form-components';
import { AddressType, FinancePurchaseType, financeSchema } from '@brighte/validation';
import { Address } from '@components/Address';
import { RepaymentTerms } from '@components/RepaymentTerms';
import { useRebateAmount } from '@hooks';
import { useApplicationData } from '@provider';
import { Alert } from '@spark-web/alert';
import { withSingleRepaymentTerm } from '@steps/FinancePurchase/utils/helpers';
import { CASH_PAYMENT, FINANCE_PURCHASE, LABELS, getFormFields, isFieldExists, productIds } from '@utils';
import FeeChargeDetails from './FeeChargeDetails';
import { MultiCategoryDetails } from './MultiCategoryDetails';
import { SingleCategoryDetails } from './SingleCategoryDetails';
import { financialProductLabel, getFeesChargeDetails, getFinanceDetails } from './utils';

type Props = Pick<UseFormReturn<FinancePurchaseType>, 'setValue' | 'register' | 'watch' | 'control'> & {
  isCashApplication: boolean;
};

export function FinancePurchaseFields({ watch, control, setValue, register, isCashApplication }: Props) {
  const { quote, Finance: finance } = useApplicationData(['quote', 'Finance']);

  const { financialProduct } = quote ?? {};
  const [repaymentTerm, ownProperty, isCurrentAddress] = watch([
    'repaymentTerm',
    'applicant.ownProperty',
    'applicant.isCurrentAddress',
  ]);
  const subsidy = useRebateAmount({ financialProductId: financialProduct?.id, quote });

  const financePurchaseDetails = getFinanceDetails({ isCashApplication, repaymentTerm, subsidy, quote });

  const feesChargeDetails = getFeesChargeDetails({ quote });

  const additionalFields = getFormFields(financeSchema, quote?.financialProduct.id ?? '');

  const defaultAddress = quote?.address ?? finance?.address;

  const isMultiCategoryApplication = Boolean(
    quote?.relatedReferrals?.length && quote?.productConfiguration?.supportMultipleCategories,
  );
  const repayments = quote?.repaymentOptions ?? [];

  const isBrightePayForm = financialProduct?.id === productIds.BRIGHTE_PAY;

  const renderPaymentSelectionDescription = () => {
    if (isMultiCategoryApplication) {
      if (isCashApplication) {
        return <Alert tone="info">{FINANCE_PURCHASE.CASH_PAYMENT_DESCRIPTION}</Alert>;
      } else {
        return <Alert tone="info">{FINANCE_PURCHASE.LOAN_PAYMENT_DESCRIPTION}</Alert>;
      }
    }
    return null;
  };

  const titleBasedOnPaymentOption = isCashApplication ? CASH_PAYMENT : financialProductLabel(financialProduct);

  // show quote details once repayment is selected or if cash application is selected
  const showQuoteDetails = withSingleRepaymentTerm(repayments) || repaymentTerm || isCashApplication;

  return (
    <>
      {renderPaymentSelectionDescription()}
      <RepaymentTerms
        name="repaymentTerm"
        control={control}
        repayments={repayments}
        setValue={setValue}
        register={register}
        isMultiCategory={Boolean(
          quote?.relatedReferrals?.length && quote?.productConfiguration?.supportMultipleCategories,
        )}
        isCashApplication={isCashApplication}
      />
      {quote && showQuoteDetails ? (
        <>
          <DataList title={titleBasedOnPaymentOption} items={financePurchaseDetails} />
          {isBrightePayForm && <FeeChargeDetails data={feesChargeDetails} />}

          {quote.relatedReferrals?.length && quote.productConfiguration?.supportMultipleCategories ? (
            <MultiCategoryDetails quote={quote} />
          ) : (
            <SingleCategoryDetails quote={quote} />
          )}
          <Address<FinancePurchaseType>
            label={LABELS.INSTALLATION_ADDRESS}
            name="address"
            setValue={setValue}
            control={control}
            disabled={!!quote.address}
            defaultValue={defaultAddress as AddressType}
          />
          {isFieldExists(additionalFields, 'applicant.ownProperty') ? (
            <ToggleField name="applicant.ownProperty" label={LABELS.I_OWN_PROPERTY} control={control} layout="inline" />
          ) : null}

          {isFieldExists(additionalFields, 'applicant.ownProperty') && ownProperty === false ? (
            <ToggleField
              name="applicant.nonHomeOwnerCheck"
              label={LABELS.NON_HOMEOWNER_CHECK}
              control={control}
              layout="inline"
            />
          ) : null}

          {isFieldExists(additionalFields, 'applicant.isCurrentAddress') && (
            <>
              <ToggleField
                name="applicant.isCurrentAddress"
                label={LABELS.IS_MOST_RECENT_ADDRESS}
                control={control}
                layout="inline"
                FieldProps={{ description: LABELS.IS_MOST_RECENT_ADDRESS_SUBTITLE }}
              />
              {isCurrentAddress === false &&
              isFieldExists(additionalFields, 'applicant.previousAddress.fullAddress') ? (
                <Address<FinancePurchaseType>
                  label={LABELS.PREVIOUS_ADDRESS}
                  name="applicant.previousAddress"
                  setValue={setValue}
                  control={control}
                  defaultValue={finance?.applicant?.previousAddress as AddressType}
                />
              ) : null}
            </>
          )}
        </>
      ) : null}
      <input type="hidden" {...register('financialProductId')} value={financialProduct?.id} />
    </>
  );
}
